import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaEye, FaTimes } from "react-icons/fa";
import BanquetGallery from "../../../components/banquet/BanquetGallery.jsx";
import BanquetPolicies from "../../../components/banquet/BanquetPolicies.jsx";
import BanquetSummary from "../../../components/banquet/BanquetSummary.jsx";
import { getCurrentBanquetOwner, logoutBanquetOwner } from "../services/banquetOwnerAuthService.js";
import { getPendingBanquetSubmissions, reviewBanquetSubmission } from "../services/banquetOwnerBanquetService.js";

const requiredFields = ["name", "slug", "venueType", "city", "locality", "address", "phone", "whatsappNumber", "email", "shortDescription", "description", "guestMin", "guestMax", "vegPrice"];

const getAddressText = (banquet) => (typeof banquet.address === "string" ? banquet.address : banquet.address?.line1 || "");

const getCompletion = (banquet) => {
  const filled = requiredFields.filter((key) => {
    if (key === "address") return getAddressText(banquet);
    return String(banquet[key] || "").trim();
  }).length;
  const extras = (banquet.images?.length ? 1 : 0) + (banquet.facilities?.length ? 1 : 0);
  return Math.round(((filled + extras) / (requiredFields.length + 2)) * 100);
};

function ReviewPreview({ banquet }) {
  return (
    <div className="space-y-6 rounded-[28px] bg-[#f5f7f4] p-3 sm:p-5">
      <BanquetGallery images={banquet.images} name={banquet.name} />
      <BanquetSummary banquet={{ ...banquet, address: getAddressText(banquet) }} distanceKm={null} />
      <BanquetPolicies banquet={{ ...banquet, address: getAddressText(banquet) }} />
    </div>
  );
}

export default function SuperAdminBanquetReviewPage() {
  const [state, setState] = useState({ isLoading: true, isSaving: false, owner: null, banquets: [], error: "", success: "" });
  const [selectedId, setSelectedId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const selectedBanquet = useMemo(() => state.banquets.find((item) => item.id === selectedId) || null, [selectedId, state.banquets]);

  const load = async () => {
    try {
      const owner = await getCurrentBanquetOwner();
      const banquets = await getPendingBanquetSubmissions({ actingOwner: owner });
      setState({ isLoading: false, isSaving: false, owner, banquets, error: "", success: "" });
      setSelectedId((current) => current || banquets[0]?.id || "");
    } catch (error) {
      setState((current) => ({ ...current, isLoading: false, error: error.message || "Unable to load banquet submissions." }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const review = async (action) => {
    if (!selectedBanquet) return;
    if (action !== "approve" && !rejectReason.trim()) {
      setState((current) => ({ ...current, error: "Please enter a reason before rejecting or sending back for edits.", success: "" }));
      return;
    }

    setState((current) => ({ ...current, isSaving: true, error: "", success: "" }));
    try {
      const updated = await reviewBanquetSubmission({
        actingOwner: state.owner,
        banquetId: selectedBanquet.id,
        action,
        rejectionReason: rejectReason,
      });
      setState((current) => ({
        ...current,
        isSaving: false,
        banquets: current.banquets.map((item) => (item.id === updated.id ? updated : item)),
        success: action === "approve" ? "Banquet approved and published." : "Banquet sent back to owner for edits.",
      }));
      setRejectReason("");
    } catch (error) {
      setState((current) => ({ ...current, isSaving: false, error: error.message || "Unable to review banquet." }));
    }
  };

  if (state.isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f5f7f4] px-4"><div className="rounded-2xl bg-white px-8 py-10 text-center shadow-sm"><h1 className="text-2xl font-semibold text-slate-900">Loading review panel</h1></div></div>;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f7f4] px-3 py-6 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex flex-col gap-4 rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[24px] sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <Link to="/" className="hover:text-emerald-600">Home</Link><span>/</span><span className="text-slate-900">Banquet Review</span>
            </div>
            <h1 className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl">Super Admin Banquet Review</h1>
            <p className="mt-2 text-sm text-slate-600">Approve, reject, or send banquet owner submissions back for edits.</p>
          </div>
          <button type="button" onClick={logoutBanquetOwner} className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700">Logout</button>
        </header>

        {state.error ? <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{state.error}</div> : null}
        {state.success ? <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{state.success}</div> : null}

        <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="min-w-0 space-y-3">
            {state.banquets.length ? state.banquets.map((banquet) => {
              const completion = getCompletion(banquet);
              return (
                <button key={banquet.id} type="button" onClick={() => setSelectedId(banquet.id)} className={`w-full overflow-hidden rounded-[20px] border bg-white text-left shadow-sm transition sm:rounded-[24px] ${selectedId === banquet.id ? "border-emerald-500" : "border-slate-200"}`}>
                  <img src={banquet.coverImage || banquet.images?.[0] || "/images/Activities.webp"} alt={banquet.name} className="h-40 w-full object-cover" />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{banquet.status}</span>
                      <span className="text-xs font-semibold text-emerald-700">{completion}% complete</span>
                    </div>
                    {banquet.hasPendingEdit ? (
                      <span className="mt-2 inline-block rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                        Live banquet — edit pending review
                      </span>
                    ) : null}
                    <h2 className="mt-3 text-lg font-semibold text-slate-900">{banquet.name || "Untitled banquet"}</h2>
                    <p className="mt-1 text-sm text-slate-600">{banquet.ownerName || banquet.ownerEmail || "Owner"} | {banquet.city || "City missing"}</p>
                    <p className="mt-2 text-xs text-slate-500">Submitted: {banquet.submittedAt?.toDate ? banquet.submittedAt.toDate().toLocaleString() : "Not submitted"}</p>
                    {banquet.lastEditedBy ? (
                      <p className="mt-1 text-xs text-slate-500">
                        Last edited by: {banquet.lastEditedBy}
                        {banquet.lastEditedAt?.toDate ? `, ${banquet.lastEditedAt.toDate().toLocaleString()}` : ""}
                      </p>
                    ) : null}
                  </div>
                </button>
              );
            }) : (
              <div className="rounded-[24px] border border-dashed border-slate-300 bg-white p-6 text-center text-sm font-semibold text-slate-600">No submissions found.</div>
            )}
          </aside>

          <main className="min-w-0 space-y-5">
            {selectedBanquet ? (
              <>
                <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[24px] sm:p-5">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-600">Manual Review</p>
                      {selectedBanquet.hasPendingEdit ? (
                        <p className="mt-1 text-xs font-semibold text-amber-700">
                          This banquet is already live. Approving will replace its live details with the edit shown below; rejecting keeps the current live details unchanged.
                        </p>
                      ) : null}
                      <h2 className="mt-2 break-words text-xl font-semibold text-slate-900 sm:text-2xl">{selectedBanquet.name}</h2>
                      <p className="mt-1 text-sm text-slate-600">{selectedBanquet.locality}, {selectedBanquet.city}</p>
                      {selectedBanquet.lastEditedBy ? (
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          Last edited by: {selectedBanquet.lastEditedBy}
                          {selectedBanquet.lastEditedAt?.toDate ? `, ${selectedBanquet.lastEditedAt.toDate().toLocaleString()}` : ""}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
                      <button type="button" disabled={state.isSaving} onClick={() => review("approve")} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"><FaCheck /> Approve</button>
                      <button type="button" disabled={state.isSaving} onClick={() => review("reject")} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"><FaTimes /> Reject</button>
                    </div>
                  </div>
                  <label className="mt-4 block">
                    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Rejection / edit reason</span>
                    <textarea value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" placeholder="Tell the owner what needs to change." />
                  </label>
                </section>
                <section className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[24px] sm:p-5">
                  <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaEye /> Full public preview</div>
                  <ReviewPreview banquet={selectedBanquet} />
                </section>
              </>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
}
